// src/pages/GiftifyInsights.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGift,
  faHeart,
  faUsers,
  faHandsHelping,
  faStar,
  faLightbulb,
  faLeaf,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";

const GiftifyInsights = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "Guest";

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState("Gift Emotions");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  const groupTabs = [
    { label: "Gift Emotions", icon: faHeart },
    { label: "Stakeholder Insights", icon: faUsers },
    { label: "Platform Ideas", icon: faLightbulb },
  ];

  const stakeholderGroups = [
    {
      group: "Gen Z / Students / Young Adults",
      emoji: "🎧",
      reason: "Expression of individuality, friendship, and digital identity",
      driver: "Trendiness, peer validation, online visibility",
      examples:
        "E-gift cards, personalized accessories, digital art, gamified gifts",
    },
    {
      group: "Homemakers / Housewives",
      emoji: "🏡",
      reason: "Relationship nurturing, family bonding, social rituals",
      driver: "Love, care, family unity, gratitude",
      examples: "Festival hampers, personalized home décor, handmade gifts",
    },
    {
      group: "Farmers / Rural Families",
      emoji: "🌾",
      reason: "Cultural and festival gifting, community bonding",
      driver: "Tradition, respect, mutual help",
      examples: "Seasonal produce packs, local handicrafts, spiritual items",
    },
    {
      group: "Corporate / MSME Owners",
      emoji: "💼",
      reason: "Relationship management, branding, loyalty",
      driver: "Professional courtesy, partnership building",
      examples:
        "Corporate hampers, branded stationery, eco-friendly gifts, local collaborations",
    },
    {
      group: "Senior Citizens",
      emoji: "👴",
      reason: "Emotional connection, nostalgia, legacy",
      driver: "Family bonding, gratitude, memory sharing",
      examples: "Memory albums, digital photo frames, health-related gifts",
    },
    {
      group: "Urban Professionals / Influencers",
      emoji: "🌆",
      reason: "Social signaling, curated experiences",
      driver: "Identity, exclusivity, emotional sophistication",
      examples: "Luxury hampers, experience vouchers, subscription boxes",
    },
  ];

  const platformIdeas = [
    {
      idea: "Emotion Tagging",
      desc: "Every gift carries an emotion — ❤️ Love | 🙏 Gratitude | 🌿 Sustainability | 💼 Professional.",
    },
    {
      idea: "AI Gift Curator",
      desc: "Smart recommendation engine for moods: ‘Thank You’, ‘Apology’, ‘Festival’, or ‘Empower Her’.",
    },
    {
      idea: "Community Gift Stores",
      desc: "Enable local women entrepreneurs to host micro-stores with curated cultural gifting themes.",
    },
    {
      idea: "Cause-Linked Gifting",
      desc: "Each purchase contributes to a cause — ‘Gift & Empower’ supports skill development or education.",
    },
    {
      idea: "Digital + Physical Fusion",
      desc: "Blend heartfelt digital gestures (NFTs, e-certificates, voice notes) with real-world gifts.",
    },
    {
      idea: "Referral-Driven Gifting",
      desc: "Encourage social gifting — reward both sender and receiver with loyalty or cashback.",
    },
  ];

  const emotionReasons = [
    { label: "To Express Love ❤️", desc: "A tangible way to show care and affection." },
    { label: "To Strengthen Bonds 🤝", desc: "Nurture relationships through meaningful gestures." },
    { label: "To Show Gratitude 🙏", desc: "A silent thank-you for help, support, or kindness." },
    { label: "To Celebrate Joy 🎉", desc: "Festivals, birthdays, and milestones become memories." },
    { label: "To Uplift Someone 🌈", desc: "A gift can heal, inspire, and empower emotionally." },
    { label: "To Reflect Identity 🌍", desc: "Gifts mirror culture, taste, and personal style." },
  ];

  const renderContent = () => {
    if (activeTab === "Gift Emotions") {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {emotionReasons.map((item, idx) => (
            <div
              key={idx}
              className="p-4 rounded-lg bg-white/10 border border-pink-300/30 shadow-md hover:bg-white/20 transition-all text-center backdrop-blur-sm"
            >
              <div className="text-m font-semibold text-pink-200 mb-2">
                {item.label}
              </div>
              <p className="text-sm text-pink-100">{item.desc}</p>
            </div>
          ))}
        </div>
      );
    } else if (activeTab === "Stakeholder Insights") {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stakeholderGroups.map((g, idx) => (
            <div
              key={idx}
              className="p-5 rounded-2xl bg-gradient-to-br from-pink-900/40 to-purple-800/40 border border-pink-400/20 shadow-lg hover:shadow-pink-400/40 transition-all"
            >
              <div className="flex items-center mb-2">
                <span className="text-2xl mr-2">{g.emoji}</span>
                <h3 className="font-semibold text-lg text-pink-100">
                  {g.group}
                </h3>
              </div>
              <p className="text-sm text-pink-200 mb-1">
                💡 <b>Why:</b> {g.reason}
              </p>
              <p className="text-sm text-pink-200 mb-1">
                🌸 <b>Driven by:</b> {g.driver}
              </p>
              <p className="text-sm text-pink-300">
                🎁 <b>Gift Ideas:</b> {g.examples}
              </p>
            </div>
          ))}
        </div>
      );
    } else {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {platformIdeas.map((idea, idx) => (
            <div
              key={idx}
              className="p-5 rounded-2xl bg-gradient-to-br from-purple-900/40 to-pink-800/40 border border-purple-300/30 shadow-md hover:bg-purple-900/60 transition-all"
            >
              <h3 className="text-pink-100 font-semibold text-lg mb-2">
                💡 {idea.idea}
              </h3>
              <p className="text-sm text-pink-200">{idea.desc}</p>
            </div>
          ))}
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-pink-900 via-purple-900 to-red-800 text-pink-50 font-sans overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-pink-950/80 backdrop-blur-md border-b border-pink-500/30 px-4 py-3 flex items-center justify-between shadow-lg">
        <div>
          <div className="text-xl font-bold text-pink-300 drop-shadow-md">
            🎁 Giftify Insights
          </div>
          <div className="text-xs text-pink-200">
            Discover the Meaning Behind Every Gift
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-sm text-pink-200">Hi, {userName} 💐</div>
              <button
                onClick={() => navigate("/giftify-dashboard")}
                className="p-2 rounded-full bg-pink-700/60 hover:bg-pink-600 transition"
                title="Dashboard"
              >
                <FontAwesomeIcon icon={faGift} className="text-pink-200" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 rounded-full bg-red-700/80 hover:bg-red-600 transition"
                title="Logout"
              >
                ⏻
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-4 py-2 rounded-full border border-pink-400/50 text-sm text-pink-100 hover:bg-pink-800/60 transition"
              >
                Login
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="px-4 py-2 rounded-full bg-pink-500 text-gray-900 text-sm hover:bg-pink-400 transition font-medium"
              >
                Join Giftify
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu */}
        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden w-10 text-xl text-pink-300 rounded-full bg-pink-800/50 p-2 hover:bg-pink-700 transition"
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>
      </header>

      {/* Tabs */}
      <div className="mt-2 px-4 py-3 bg-pink-950/40 border-b border-pink-400/30">
        <div className="grid grid-cols-3 gap-2 place-items-center">
          {groupTabs.map((tab) => (
            <button
              key={tab.label}
              onClick={() => setActiveTab(tab.label)}
              className={`flex flex-col items-center w-full px-3 py-3 rounded-lg text-xs font-medium transition-all backdrop-blur-sm shadow-md hover:scale-105 ${
                activeTab === tab.label
                  ? "bg-pink-600/80 text-yellow-200 border-2 border-yellow-300"
                  : "bg-pink-800/40 text-pink-100 hover:bg-pink-700/50"
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="text-lg mb-1" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-grow mx-auto px-4 py-6 w-full max-w-6xl">
        {renderContent()}
      </main>

      {/* Footer */}
      <footer className="bg-pink-950/80 border-t border-pink-400/30 text-pink-200 py-5 text-center text-sm">
        © 2025 Think5Whys Giftify • Empowering Emotions Through Gifts 🌸
      </footer>
    </div>
  );
};

export default GiftifyInsights;
